import React, { useCallback, useEffect, useState } from 'react';
import { Search, Download, Banknote, X } from 'lucide-react';
import api from '../../lib/api';
import { toast } from '../../hooks/use-toast';
import { formatCurrency } from '../../mock';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';

const STATUS = [['', 'Todos'], ['pending', 'A pagar'], ['paid', 'Pagos']];
const EMPTY = { q: '', status: '', bank: '' };

// Finance → Payoffs: quitação dos financiamentos dos veículos recebidos em trade-in.
export const Payoffs = () => {
  const [f, setF] = useState(EMPTY);
  const [data, setData] = useState({ rows: [], metrics: {}, banks: [], methods: [], can_pay: false });
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [payRow, setPayRow] = useState(null);
  const [form, setForm] = useState({ amount: '', method: '', reference: '', note: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(Object.entries(f).filter(([, v]) => v));
      const { data: d } = await api.get(`/finance/payoffs?${params.toString()}`);
      setData(d);
    } catch (e) { if (e.response?.status === 403) setDenied(true); }
    finally { setLoading(false); }
  }, [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openPay = (r) => {
    setPayRow(r);
    setForm({ amount: String(r.payoff || ''), method: data.methods?.[0] || 'Check', reference: '', note: '' });
  };

  const pay = async () => {
    setBusy(true);
    try {
      await api.post(`/finance/payoffs/${payRow.trade_in_id}/pay`, { ...form, amount: Number(form.amount || 0) });
      toast({ title: 'Payoff pago', description: `${payRow.cliente} · ${formatCurrency(Number(form.amount || 0))} via ${form.method}` });
      setPayRow(null);
      await load();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setBusy(false); }
  };

  const exportCsv = () => {
    const head = ['Cliente', 'Veiculo', 'VIN', 'Banco', 'Financiamento', 'Payoff', 'Avaliado', 'Destino', 'Stock', 'Criado em', 'Criado por', 'Pago', 'Valor pago', 'Metodo', 'Referencia', 'Pago em', 'Pago por'];
    const rows = data.rows.map((r) => [r.cliente, r.veiculo, r.vin, r.payoff_bank, r.payoff_financing, r.payoff,
      r.appraised_value, r.disposition, r.stock, (r.created_at || '').slice(0, 10), r.created_by,
      r.paid ? 'Sim' : 'Nao', r.paid_amount, r.paid_method, r.reference, (r.paid_at || '').slice(0, 10), r.paid_by]);
    const csv = [head, ...rows].map((l) => l.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }));
    a.download = `payoffs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (denied) return <div className="ent-panel p-6 text-sm text-muted-foreground" data-testid="payoffs-denied">Somente usuários com acesso financeiro veem os Payoffs.</div>;

  const m = data.metrics || {};
  const active = Object.values(f).some((v) => v);

  return (
    <div className="space-y-3" data-testid="payoffs-page">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border border border-border rounded-sm overflow-hidden" data-testid="payoffs-metrics">
        <div className="bg-card px-3 py-2"><p className="ent-label">Payoffs a pagar</p><p className="ent-value mt-1 t-warn" data-testid="po-pending">{m.pending ?? 0}</p></div>
        <div className="bg-card px-3 py-2"><p className="ent-label">Valor a pagar</p><p className="ent-value mt-1 t-warn" data-testid="po-pending-amount">{formatCurrency(m.pending_amount || 0)}</p></div>
        <div className="bg-card px-3 py-2"><p className="ent-label">Pagos no mês</p><p className="ent-value mt-1 t-ok">{m.paid_month ?? 0}</p></div>
        <div className="bg-card px-3 py-2"><p className="ent-label">Valor pago no mês</p><p className="ent-value mt-1 t-ok" data-testid="po-paid-amount">{formatCurrency(m.paid_amount_month || 0)}</p></div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-y border-border py-2.5">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
          <input data-testid="po-search" className="ent-input pl-8" style={{ width: 240 }} value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Cliente, veículo, VIN, banco..." />
        </div>
        {STATUS.map(([k, l]) => (
          <button key={k || 'all'} data-testid={`po-status-${k || 'all'}`} onClick={() => setF({ ...f, status: k })}
            className={`ent-btn ent-btn-sm ${f.status === k ? 'ent-btn-primary' : ''}`}>{l}</button>
        ))}
        <select data-testid="po-bank" className="ent-input" style={{ width: 170 }} value={f.bank} onChange={(e) => setF({ ...f, bank: e.target.value })}>
          <option value="">Todos os bancos</option>
          {(data.banks || []).map((b) => <option key={b} value={b} className="bg-popover">{b}</option>)}
        </select>
        {active && <button data-testid="po-clear" onClick={() => setF(EMPTY)} className="ent-btn ent-btn-sm"><X className="w-3 h-3" /> Limpar</button>}
        <button data-testid="po-export" onClick={exportCsv} disabled={!data.rows.length} className="ent-btn ent-btn-sm ml-auto disabled:opacity-40"><Download className="w-3 h-3" /> CSV</button>
      </div>

      <div className="overflow-x-auto">
        <table className="ent-table" data-testid="payoffs-table">
          <thead><tr>
            <th>Cliente</th><th>Veículo</th><th>Banco / financiamento</th><th className="text-right">Payoff</th>
            <th>Avaliação</th><th>Status</th><th />
          </tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.trade_in_id} data-testid={`po-row-${r.trade_in_id}`}>
                <td className="font-medium max-w-[160px] truncate">{r.cliente}
                  <span className="block text-[10px] t-label">{(r.created_at || '').slice(0, 10)} · {r.created_by}</span>
                </td>
                <td className="max-w-[200px]"><span className="truncate block">{r.veiculo}</span>
                  <span className="mono text-[10px] t-label">{r.vin ? r.vin.slice(-8) : '—'}{r.stock ? ` · #${r.stock}` : ''}</span>
                </td>
                <td className="max-w-[170px]"><span className="truncate block text-[12px]">{r.payoff_bank || '—'}</span>
                  <span className="mono text-[10px] t-label">{r.payoff_financing || '—'}</span>
                </td>
                <td className="num text-right font-semibold">{formatCurrency(r.payoff)}
                  {r.paid && Math.abs((r.paid_amount || 0) - (r.payoff || 0)) > 0.5 && (
                    <span className="block text-[10px] t-warn">pago {formatCurrency(r.paid_amount)}</span>
                  )}
                </td>
                <td className="num text-[11px]">{formatCurrency(r.appraised_value)}<span className="block text-[10px] t-label">{r.disposition}</span></td>
                <td>
                  {r.paid ? (
                    <>
                      <span className="ent-status t-ok"><span className="ent-dot" />Pago</span>
                      <span className="block text-[10px] t-label" data-testid={`po-paidinfo-${r.trade_in_id}`}>
                        Payoff pago por {r.paid_by} em {(r.paid_at || '').slice(0, 10)} · {r.paid_method}{r.reference ? ` · ${r.reference}` : ''}
                      </span>
                    </>
                  ) : <span className="ent-status t-warn"><span className="ent-dot" />A pagar</span>}
                </td>
                <td className="text-right whitespace-nowrap">
                  {!r.paid && data.can_pay && (
                    <button data-testid={`po-pay-${r.trade_in_id}`} onClick={() => openPay(r)} className="ent-btn ent-btn-sm"><Banknote className="w-3 h-3" /> Payoff</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && !data.rows.length && <p className="ent-empty" data-testid="payoffs-empty">Nenhum payoff. Os registros aparecem aqui quando um trade-in é salvo com a chave de payoff ativada.</p>}
        {loading && <p className="ent-empty">Carregando payoffs...</p>}
      </div>

      <Dialog open={!!payRow} onOpenChange={(o) => !o && setPayRow(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md" data-testid="po-pay-dialog">
          <DialogHeader><DialogTitle className="font-display font-bold text-lg uppercase tracking-tight flex items-center gap-2"><Banknote className="w-4 h-4 text-primary" /> Pagar payoff</DialogTitle></DialogHeader>
          <div className="space-y-2.5 text-[13px]">
            <div className="grid grid-cols-2 gap-y-1.5">
              <span className="t-label">Cliente</span><span className="font-medium text-right">{payRow?.cliente}</span>
              <span className="t-label">Veículo</span><span className="text-right">{payRow?.veiculo}</span>
              <span className="t-label">VIN</span><span className="text-right mono text-[11px]">{payRow?.vin || '—'}</span>
              <span className="t-label">Banco / credor</span><span className="text-right">{payRow?.payoff_bank || '—'}</span>
              <span className="t-label">Financiamento</span><span className="text-right mono text-[11px]">{payRow?.payoff_financing || '—'}</span>
              <span className="t-label">Payoff informado</span><span className="text-right num font-semibold">{formatCurrency(payRow?.payoff || 0)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="ent-label mb-1">Valor pago *</p>
                <input data-testid="po-pay-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="w-full bg-muted/40 border border-border rounded-md px-3 py-2 text-[14px] num outline-none focus:border-primary" />
              </div>
              <div>
                <p className="ent-label mb-1">Método *</p>
                <select data-testid="po-pay-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}
                  className="w-full bg-muted/40 border border-border rounded-md px-3 py-2 text-[13px] outline-none focus:border-primary">
                  {(data.methods || []).map((x) => <option key={x} value={x} className="bg-popover">{x}</option>)}
                </select>
              </div>
            </div>
            <div>
              <p className="ent-label mb-1">Referência (nº do cheque / transferência)</p>
              <input data-testid="po-pay-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
                className="w-full bg-muted/40 border border-border rounded-md px-3 py-2 text-[13px] outline-none focus:border-primary" />
            </div>
            <div>
              <p className="ent-label mb-1">Observação</p>
              <input data-testid="po-pay-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="w-full bg-muted/40 border border-border rounded-md px-3 py-2 text-[13px] outline-none focus:border-primary" />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="po-pay-cancel" onClick={() => setPayRow(null)} className="ent-btn">Voltar</button>
            <button data-testid="po-pay-save" onClick={pay} disabled={busy} className="ent-btn ent-btn-primary">{busy ? 'Registrando...' : 'Confirmar pagamento'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payoffs;
